import { parseTradingMethodSpec } from "./trading-methods.ts";
import type { BacktestFormState } from "./types.ts";
import type { StrategyRuleDSL, StrategyRuleOperand, StrategyRuleCondition } from "./strategy-builder.ts";
import type { LocalHistoryStorage } from "./local-history.ts";

export const LOCAL_DRAFT_PREFIX = "backtest.web.draft.v1";
export const LOCAL_DRAFT_TTL_MS = 30 * 24 * 60 * 60 * 1000;
export const LOCAL_DRAFT_MAX_BYTES = 64 * 1024;
const MAX_SCOPES = 8;
const STORE_KEY = `${LOCAL_DRAFT_PREFIX}.store`;
export const LOCAL_DRAFT_MAX_STORE_BYTES = MAX_SCOPES * LOCAL_DRAFT_MAX_BYTES + 64;

export interface LocalDraft {
  form: BacktestFormState;
  savedAt: number;
}

function object(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}
function text(value: unknown, max: number): value is string {
  return typeof value === "string" && value.length <= max;
}
function finite(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && Math.abs(value) <= 1e15;
}
function choice(value: unknown, values: readonly string[]): boolean {
  return typeof value === "string" && values.includes(value);
}
function identifier(value: unknown): value is string {
  return typeof value === "string" && /^[A-Za-z0-9_.:-]{1,96}$/.test(value)
    && !["__proto__", "constructor", "prototype"].includes(value);
}

/** Structural draft validation only. Catalog/server validation remains required. */
function draftDSL(value: unknown): StrategyRuleDSL | null {
  if (!object(value) || value.schema_version !== 2 || !identifier(value.catalog_version)
    || value.short_mode !== "mirror_long" || !object(value.risk) || value.risk.mode !== "inherit") return null;
  const operand = (input: unknown): StrategyRuleOperand | null => {
    if (!object(input)) return null;
    if (input.kind === "number" && finite(input.value)) return { kind: "number", value: input.value };
    if (input.kind !== "indicator" || !identifier(input.id) || !object(input.parameters)
      || Object.keys(input.parameters).length > 16) return null;
    const parameters: Record<string, number> = {};
    for (const [key, entry] of Object.entries(input.parameters)) {
      if (!identifier(key) || !finite(entry)) return null;
      parameters[key] = entry;
    }
    return { kind: "indicator", id: input.id, parameters };
  };
  const seen = new Set<string>();
  const group = (input: unknown, id: "entry" | "exit"): StrategyRuleDSL["entry"] | null => {
    if (!object(input) || input.id !== id || !choice(input.match, ["all", "any"])
      || !Array.isArray(input.conditions) || input.conditions.length > 32) return null;
    const conditions: StrategyRuleCondition[] = [];
    for (const item of input.conditions) {
      if (!object(item) || !identifier(item.id) || seen.has(item.id)
        || !choice(item.operator, ["gt", "gte", "lt", "lte", "crosses_above", "crosses_below", "rising", "falling"])) return null;
      const left = operand(item.left);
      const right = item.right === undefined ? undefined : operand(item.right);
      if (!left || left.kind !== "indicator" || right === null) return null;
      seen.add(item.id);
      conditions.push({ id: item.id, operator: item.operator as StrategyRuleCondition["operator"], left, ...(right ? { right } : {}) });
    }
    return { id, match: input.match as "all" | "any", conditions };
  };
  const entry = group(value.entry, "entry");
  const exit = group(value.exit, "exit");
  if (!entry || !exit) return null;
  return { schema_version: 2, catalog_version: value.catalog_version, short_mode: "mirror_long", risk: { mode: "inherit" }, entry, exit };
}

export function parseLocalDraftForm(value: unknown): BacktestFormState | null {
  if (!object(value) || !text(value.symbol, 32) || !/^[A-Za-z0-9=^/._-]{1,32}$/.test(value.symbol)
    || !choice(value.timeframe, ["5m", "15m", "30m", "1h", "4h", "1d"])
    || !choice(value.period, ["1M", "3M", "6M", "1Y", "Custom"])
    || !choice(value.strategyMode, ["auto", "manual", "builder", "method"])
    || !text(value.strategyName, 256) || !text(value.prompt, 16_000)
    || !choice(value.executionModel, ["spot", "futures"]) || !choice(value.marginMode, ["isolated", "cross"])
    || !choice(value.tradeDirection, ["long", "short", "both"])
    || !choice(value.exitMode, ["tp_sl_only", "signal_and_risk", "signal_only"])
    || typeof value.useAI !== "boolean" || !text(value.startDate, 64) || !text(value.endDate, 64)) return null;
  const numericFields = ["initialCapital", "commissionRate", "slippageBps", "positionSizePct", "leverage", "maintenanceMarginPercent", "fundingRatePercent", "fundingIntervalHours", "liquidationFeePercent", "stopLossPct", "takeProfitPct"] as const;
  if (!numericFields.every((key) => finite(value[key]))) return null;
  const stringFields = ["symbol", "timeframe", "period", "strategyMode", "strategyName", "prompt", "executionModel", "marginMode", "tradeDirection", "exitMode", "startDate", "endDate"] as const;
  const result = Object.fromEntries([...stringFields, ...numericFields, "useAI"].map((key) => [key, value[key]])) as unknown as BacktestFormState;
  if (value.provider != null) {
    if (!choice(value.provider, ["auto", "binance", "binance_futures", "yfinance", "yahoo"])) return null;
    result.provider = value.provider as string;
  }
  if (value.market != null) {
    if (!choice(value.market, ["auto", "crypto", "crypto_futures", "forex", "futures", "metal", "equity", "index", "futures_index"])) return null;
    result.market = value.market as string;
  }
  if (value.restoredDateRange !== undefined) {
    const range = value.restoredDateRange;
    if (!object(range) || !text(range.start, 64) || !text(range.end, 64)
      || !Number.isFinite(Date.parse(range.start)) || !Number.isFinite(Date.parse(range.end))
      || Date.parse(range.start) > Date.parse(range.end)
      || !text(range.inputStart, 10) || !/^\d{4}-\d{2}-\d{2}$/.test(range.inputStart)
      || !text(range.inputEnd, 10) || !/^\d{4}-\d{2}-\d{2}$/.test(range.inputEnd)) return null;
    result.restoredDateRange = { start: range.start, end: range.end, inputStart: range.inputStart, inputEnd: range.inputEnd };
  }
  if (value.strategyMethod !== undefined || value.strategyMode === "method") {
    const parsed = parseTradingMethodSpec(value.strategyMethod);
    if (!parsed || value.strategyMode !== "method" || value.strategyDSL !== undefined) return null;
    result.strategyMethod = parsed;
  }
  if (value.unsupportedMethodExecution !== undefined) {
    const policy = value.unsupportedMethodExecution;
    if (value.strategyMode !== "method" || !object(policy) || !text(policy.riskMode, 120) || !text(policy.exitMode, 120)) return null;
    result.unsupportedMethodExecution = { riskMode: policy.riskMode, exitMode: policy.exitMode };
  }
  if (value.strategyDSL !== undefined) {
    const parsed = draftDSL(value.strategyDSL);
    if (!parsed) return null;
    result.strategyDSL = parsed;
  }
  return result;
}

export function localDraftKey(origin: string, accountID: string | null): string {
  const url = new URL(origin);
  if (!/^https?:$/.test(url.protocol) || url.username || url.password || url.search || url.hash
    || (url.pathname !== "/" && url.pathname !== "") || (accountID?.length ?? 0) > 256) throw new Error("invalid_draft_scope");
  return `${LOCAL_DRAFT_PREFIX}.entry.${encodeURIComponent(url.origin)}.${accountID ? `account.${encodeURIComponent(accountID)}` : "guest"}`;
}

interface StoredDraft extends LocalDraft {
  key: string;
  origin: string;
  accountID: string | null;
}

interface DraftEnvelopeState {
  entries: StoredDraft[];
  writable: boolean;
}

function encodedSize(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

function envelope(storage: LocalHistoryStorage): DraftEnvelopeState {
  // Keep storage access outside the parsing fallback: denied storage must not
  // be mistaken for an empty store which is then safe to overwrite.
  const raw = storage.getItem(STORE_KEY);
  if (!raw) return { entries: [], writable: true };
  if (raw.length > LOCAL_DRAFT_MAX_STORE_BYTES || encodedSize(raw) > LOCAL_DRAFT_MAX_STORE_BYTES) {
    // Do not parse unbounded data or overwrite a potentially newer schema.
    return { entries: [], writable: false };
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    // An older bundle must never replace a newer schema it cannot understand.
    if (object(parsed) && typeof parsed.schema === "number" && parsed.schema > 1) {
      return { entries: [], writable: false };
    }
    if (!object(parsed) || parsed.schema !== 1 || !Array.isArray(parsed.entries)
      || parsed.entries.length > MAX_SCOPES) return { entries: [], writable: true };
    const entries: StoredDraft[] = [];
    const seen = new Set<string>();
    for (const item of parsed.entries) {
      if (!object(item) || typeof item.key !== "string" || typeof item.origin !== "string"
        || !(item.accountID === null || typeof item.accountID === "string")
        || !finite(item.savedAt) || item.savedAt < 0
        || encodedSize(JSON.stringify(item)) > LOCAL_DRAFT_MAX_BYTES
        || new URL(item.origin).origin !== item.origin
        || localDraftKey(item.origin, item.accountID) !== item.key || seen.has(item.key)) {
        return { entries: [], writable: true };
      }
      const form = parseLocalDraftForm(item.form);
      if (!form) return { entries: [], writable: true };
      seen.add(item.key);
      entries.push({ key: item.key, origin: item.origin, accountID: item.accountID, savedAt: item.savedAt, form });
    }
    return { entries, writable: true };
  } catch { return { entries: [], writable: true }; }
}

function fresh(entry: StoredDraft, now: number): boolean {
  return entry.savedAt <= now && now - entry.savedAt < LOCAL_DRAFT_TTL_MS;
}

function writeEnvelope(storage: LocalHistoryStorage, entries: StoredDraft[]): boolean {
  if (entries.length > MAX_SCOPES || entries.some((entry) => encodedSize(JSON.stringify(entry)) > LOCAL_DRAFT_MAX_BYTES)) {
    return false;
  }
  const raw = JSON.stringify({ schema: 1, entries });
  if (encodedSize(raw) > LOCAL_DRAFT_MAX_STORE_BYTES) return false;
  // One physical key and one atomic write: tab races are last-writer-wins,
  // never a partially updated index plus orphaned private draft records.
  storage.setItem(STORE_KEY, raw);
  return true;
}

export function readLocalDraft(storage: LocalHistoryStorage | null, origin: string, accountID: string | null, now = Date.now()): LocalDraft | null {
  if (!storage || !finite(now) || now < 0) return null;
  try {
    const key = localDraftKey(origin, accountID);
    const entry = envelope(storage).entries.find((item) => item.key === key);
    // Reads never rewrite a stale tab's envelope. Expired/future entries are
    // unavailable immediately and are pruned by the next successful save.
    return entry && fresh(entry, now) ? { form: entry.form, savedAt: entry.savedAt } : null;
  } catch { return null; }
}

export function saveLocalDraft(storage: LocalHistoryStorage | null, origin: string, accountID: string | null, form: BacktestFormState, now = Date.now()): boolean {
  if (!storage) return false;
  try {
    const parsed = parseLocalDraftForm(form);
    if (!parsed || !finite(now) || now < 0) return false;
    const key = localDraftKey(origin, accountID);
    const next: StoredDraft = {
      key, origin: new URL(origin).origin, accountID: accountID || null, savedAt: now, form: parsed,
    };
    if (encodedSize(JSON.stringify(next)) > LOCAL_DRAFT_MAX_BYTES) return false;
    const current = envelope(storage);
    if (!current.writable) return false;
    const retained = current.entries.filter((item) => item.key !== key && fresh(item, now))
      .sort((a, b) => b.savedAt - a.savedAt).slice(0, MAX_SCOPES - 1);
    return writeEnvelope(storage, [next, ...retained]);
  } catch { return false; }
}

export function removeLocalDraft(storage: LocalHistoryStorage | null, origin: string, accountID: string | null): boolean {
  if (!storage) return false;
  try {
    const key = localDraftKey(origin, accountID);
    const current = envelope(storage);
    if (!current.writable) return false;
    return writeEnvelope(storage, current.entries.filter((item) => item.key !== key));
  } catch { return false; }
}

export function localDraftStorage(): LocalHistoryStorage | null {
  try { return typeof window === "undefined" ? null : window.localStorage; } catch { return null; }
}
